import { ShieldWarning, WarningCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import useRestore from "./useRestore";
import RestoreSourceForm from "./RestoreSourceForm";
import RestoreProgress from "./RestoreProgress";

/**
 * Modal de RESTORE. Orquestrador enxuto:
 *   - `useRestore` cuida do state e da chamada ao backend
 *   - `RestoreSourceForm` cuida do input (upload/drive)
 *   - `RestoreProgress` é o overlay durante processamento
 */
export default function RestoreModal({ open, onClose, driveConfigured, onRestored }) {
  const r = useRestore({ onClose, onRestored });
  if (!open) return null;

  const executeBtnColor = r.canConfirm() ? "#E60000" : "#999";

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
        onClick={r.handleClose}
      >
        <div
          className="bg-white border-2 border-[#E60000] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="backup-restore-modal"
        >
          <RestoreHeader />

          <div className="p-6 space-y-4">
            <WarningBox />

            <RestoreSourceForm
              mode={r.mode}
              driveConfigured={driveConfigured}
              restoring={r.restoring}
              file={r.file}
              setFile={r.setFile}
              driveFiles={r.driveFiles}
              driveFilesLoading={r.driveFilesLoading}
              selectedDriveFile={r.selectedDriveFile}
              setSelectedDriveFile={r.setSelectedDriveFile}
              switchMode={r.switchMode}
            />

            <ConfirmInput value={r.confirm} onChange={r.setConfirm} disabled={r.restoring} />
          </div>

          <div className="border-t border-black px-6 py-3 flex flex-wrap items-center gap-2 justify-end bg-zinc-50">
            <button
              className="btn btn-ghost"
              onClick={r.handleClose}
              disabled={r.restoring}
              data-testid="restore-cancel"
            >
              Cancelar
            </button>
            <button
              className="btn"
              style={{ backgroundColor: executeBtnColor, color: "white" }}
              onClick={r.execute}
              disabled={!r.canConfirm() || r.restoring}
              data-testid="restore-execute"
            >
              <ArrowCounterClockwise size={14} weight="bold" />
              {r.restoring ? "Restaurando..." : "Restaurar Agora"}
            </button>
          </div>
        </div>
      </div>

      {r.restoring && <RestoreProgress />}
    </>
  );
}

function RestoreHeader() {
  return (
    <div className="bg-[#E60000] text-white px-6 py-4 flex items-center gap-3">
      <ShieldWarning size={24} weight="fill" />
      <div>
        <div className="font-head font-bold text-lg leading-tight">Restaurar Backup</div>
        <div className="text-xs opacity-90 mt-1">Operação destrutiva — vai substituir todos os dados atuais</div>
      </div>
    </div>
  );
}

function WarningBox() {
  return (
    <div className="border border-[#E60000] bg-[#E60000]/10 p-4 text-sm">
      <div className="font-bold mb-2 flex items-center gap-2">
        <WarningCircle size={16} weight="fill" /> Antes de continuar
      </div>
      <ul className="text-xs space-y-1 list-disc pl-5">
        <li>O sistema vai gerar automaticamente um <strong>backup de segurança</strong> ANTES de restaurar (armazenado localmente).</li>
        <li>Todas as coleções serão <strong>substituídas</strong> pelo conteúdo do arquivo (mongorestore --drop).</li>
        <li>As configurações de backup (Drive/e-mail/agendamento) e o próprio histórico NÃO são sobrescritos.</li>
        <li>Aguarde a conclusão sem fechar o navegador.</li>
      </ul>
    </div>
  );
}

function ConfirmInput({ value, onChange, disabled }) {
  return (
    <div className="border-t border-zinc-200 pt-4">
      <label className="label">
        Para confirmar, digite <span className="font-mono bg-[#E60000] text-white px-1">RESTAURAR</span> abaixo:
      </label>
      <input
        type="text"
        className="input font-mono uppercase"
        placeholder="RESTAURAR"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        disabled={disabled}
        data-testid="restore-confirm-input"
        autoComplete="off"
      />
    </div>
  );
}
